import dagre from '@dagrejs/dagre';
import { Position } from '@xyflow/react';
import { MindmapNodeShape, type MindmapTreeNode, type MindmapFlowNode, type MindmapFlowEdge } from './mindmap-types';
import {
  DEPTH_COLORS,
  DAGRE_NODESEP,
  DAGRE_RANKSEP,
  NODE_TEXT_CHAR_WIDTH,
  NODE_MIN_WIDTH,
  NODE_BASE_HEIGHT,
  NODE_HORIZONTAL_PADDING,
  ROOT_NODE_HORIZONTAL_PADDING,
  ROOT_NODE_MIN_WIDTH,
  ROOT_NODE_HEIGHT,
} from './mindmap-constants';

const DAGRE_RANK_DIRECTION_LEFT_TO_RIGHT = 'LR';
const MINDMAP_CARD_NODE_TYPE = 'mindmapCard';
const MINDMAP_CURVE_EDGE_TYPE = 'mindmapCurve';

interface DagreNodeData {
  width: number;
  height: number;
}

interface DagrePositionedNodeData extends DagreNodeData {
  x: number;
  y: number;
}

interface DagreGraphData {
  rankdir: typeof DAGRE_RANK_DIRECTION_LEFT_TO_RIGHT;
  nodesep: number;
  ranksep: number;
  marginx: number;
  marginy: number;
  width?: number;
  height?: number;
}

interface NodeSize {
  width: number;
  height: number;
}

interface VisibleTreeEdge {
  source: MindmapTreeNode;
  target: MindmapTreeNode;
}

interface VisibleTreeElements {
  nodes: MindmapTreeNode[];
  edges: VisibleTreeEdge[];
}

export interface LayoutResult {
  nodes: MindmapFlowNode[];
  edges: MindmapFlowEdge[];
  graphWidth: number;
  graphHeight: number;
}

const SHAPE_SIZE_TUNING = {
  CircleMinDiameter: 72,
  HexagonExtraWidth: 22,
  CloudExtraWidth: 12,
  CloudExtraHeight: 6,
} as const;

function getNodeSize(node: MindmapTreeNode): NodeSize {
  const labelWidth = node.label.length * NODE_TEXT_CHAR_WIDTH;
  const horizontalPadding = node.depth === 0 ? ROOT_NODE_HORIZONTAL_PADDING : NODE_HORIZONTAL_PADDING;
  const baseWidth = Math.max(NODE_MIN_WIDTH, labelWidth + horizontalPadding * 2);

  if (node.depth === 0) {
    return {
      width: Math.max(ROOT_NODE_MIN_WIDTH, baseWidth),
      height: ROOT_NODE_HEIGHT,
    };
  }

  if (node.shape === MindmapNodeShape.Circle) {
    const circleDiameter = Math.max(SHAPE_SIZE_TUNING.CircleMinDiameter, baseWidth);
    return {
      width: circleDiameter,
      height: circleDiameter,
    };
  }

  if (node.shape === MindmapNodeShape.Hexagon) {
    return {
      width: baseWidth + SHAPE_SIZE_TUNING.HexagonExtraWidth,
      height: NODE_BASE_HEIGHT,
    };
  }

  if (node.shape === MindmapNodeShape.Cloud) {
    return {
      width: baseWidth + SHAPE_SIZE_TUNING.CloudExtraWidth,
      height: NODE_BASE_HEIGHT + SHAPE_SIZE_TUNING.CloudExtraHeight,
    };
  }

  return {
    width: baseWidth,
    height: NODE_BASE_HEIGHT,
  };
}

function collectVisibleTreeElements(input: {
  root: MindmapTreeNode;
  expandedNodeIds: Set<string>;
}): VisibleTreeElements {
  const { root, expandedNodeIds } = input;
  const visibleNodes: MindmapTreeNode[] = [];
  const visibleEdges: VisibleTreeEdge[] = [];

  const walkVisibleTree = (node: MindmapTreeNode): void => {
    visibleNodes.push(node);

    if (!expandedNodeIds.has(node.id)) {
      return;
    }

    node.children.forEach((child) => {
      visibleEdges.push({ source: node, target: child });
      walkVisibleTree(child);
    });
  };

  walkVisibleTree(root);

  return {
    nodes: visibleNodes,
    edges: visibleEdges,
  };
}

export function findMindmapNodeById(input: {
  root: MindmapTreeNode;
  nodeId: string;
}): MindmapTreeNode | null {
  const { root, nodeId } = input;

  if (root.id === nodeId) {
    return root;
  }

  for (const child of root.children) {
    const foundInSubtree = findMindmapNodeById({
      root: child,
      nodeId,
    });
    if (foundInSubtree) {
      return foundInSubtree;
    }
  }

  return null;
}

export function buildParentMap(root: MindmapTreeNode): Map<string, string> {
  const parentMap = new Map<string, string>();

  const walk = (node: MindmapTreeNode, parentId: string | null): void => {
    if (parentId) {
      parentMap.set(node.id, parentId);
    }

    node.children.forEach((child) => {
      walk(child, node.id);
    });
  };

  walk(root, null);

  return parentMap;
}

export function getAncestorPath(input: {
  nodeId: string;
  parentMap: Map<string, string>;
}): string[] {
  const ancestors: string[] = [];
  const visitedNodeIds = new Set<string>();
  let currentNodeId: string | undefined = input.nodeId;

  while (currentNodeId && !visitedNodeIds.has(currentNodeId)) {
    ancestors.push(currentNodeId);
    visitedNodeIds.add(currentNodeId);
    currentNodeId = input.parentMap.get(currentNodeId);
  }

  return ancestors;
}

export function findSiblingNodeId(input: {
  root: MindmapTreeNode;
  nodeId: string;
  parentMap: Map<string, string>;
  offset: 1 | -1;
}): string | null {
  const parentNodeId = input.parentMap.get(input.nodeId);
  if (!parentNodeId) {
    return null;
  }

  const parentNode = findMindmapNodeById({
    root: input.root,
    nodeId: parentNodeId,
  });
  if (!parentNode) {
    return null;
  }

  const siblingIndex = parentNode.children.findIndex((child) => child.id === input.nodeId);
  if (siblingIndex === -1) {
    return null;
  }

  const targetSiblingIndex = siblingIndex + input.offset;
  if (targetSiblingIndex < 0 || targetSiblingIndex >= parentNode.children.length) {
    return null;
  }

  return parentNode.children[targetSiblingIndex]?.id ?? null;
}

export function collectExpandableNodeIds(root: MindmapTreeNode): Set<string> {
  const expandableNodeIds = new Set<string>();

  const walk = (node: MindmapTreeNode): void => {
    if (node.children.length > 0) {
      expandableNodeIds.add(node.id);
    }

    node.children.forEach((child) => walk(child));
  };

  walk(root);
  return expandableNodeIds;
}

export function getVisibleSubtreeIds(input: {
  root: MindmapTreeNode;
  nodeId: string;
  expandedNodeIds: Set<string>;
}): Set<string> {
  const targetNode = findMindmapNodeById({
    root: input.root,
    nodeId: input.nodeId,
  });

  if (!targetNode) {
    return new Set<string>();
  }

  const visibleSubtreeIds = new Set<string>();

  const walkVisibleSubtree = (node: MindmapTreeNode): void => {
    visibleSubtreeIds.add(node.id);

    if (!input.expandedNodeIds.has(node.id)) {
      return;
    }

    node.children.forEach((child) => walkVisibleSubtree(child));
  };

  walkVisibleSubtree(targetNode);

  return visibleSubtreeIds;
}

export function getVisibleDescendantIds(input: {
  root: MindmapTreeNode;
  nodeId: string;
  expandedNodeIds: Set<string>;
}): Set<string> {
  const visibleSubtreeIds = getVisibleSubtreeIds(input);
  visibleSubtreeIds.delete(input.nodeId);
  return visibleSubtreeIds;
}

export function buildMindmapLayout(root: MindmapTreeNode, expandedNodeIds: Set<string>): LayoutResult {
  const { nodes: visibleNodes, edges: visibleEdges } = collectVisibleTreeElements({
    root,
    expandedNodeIds,
  });

  const dagreGraph = new dagre.graphlib.Graph<DagreGraphData, DagreNodeData>({
    multigraph: true,
  });
  dagreGraph.setGraph({
    rankdir: DAGRE_RANK_DIRECTION_LEFT_TO_RIGHT,
    nodesep: DAGRE_NODESEP,
    ranksep: DAGRE_RANKSEP,
    marginx: 20,
    marginy: 20,
  });
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  const nodeSizeMap = new Map<string, NodeSize>();
  visibleNodes.forEach((node) => {
    const size = getNodeSize(node);
    nodeSizeMap.set(node.id, size);
    dagreGraph.setNode(node.id, size);
  });

  visibleEdges.forEach((edge) => {
    dagreGraph.setEdge(edge.source.id, edge.target.id);
  });

  dagre.layout(dagreGraph);

  const nodes: MindmapFlowNode[] = visibleNodes.map((node) => {
    const dagreNode = dagreGraph.node(node.id) as DagrePositionedNodeData | undefined;
    const size = nodeSizeMap.get(node.id) ?? getNodeSize(node);
    const x = dagreNode ? dagreNode.x - dagreNode.width / 2 : 0;
    const y = dagreNode ? dagreNode.y - dagreNode.height / 2 : 0;

    return {
      id: node.id,
      type: MINDMAP_CARD_NODE_TYPE,
      position: { x, y },
      width: size.width,
      height: size.height,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      data: {
        nodeId: node.id,
        label: node.label,
        shape: node.shape,
        depth: node.depth,
        childCount: node.children.length,
        isExpanded: expandedNodeIds.has(node.id),
        isRoot: node.depth === 0,
        colorIndex: node.depth % DEPTH_COLORS.length,
      },
      style: {
        width: size.width,
        height: size.height,
      },
    };
  });

  const edges: MindmapFlowEdge[] = visibleEdges.map((edge, edgeIndex) => {
    return {
      id: `mindmap-edge-${edge.source.id}-${edge.target.id}-${edgeIndex}`,
      source: edge.source.id,
      target: edge.target.id,
      type: MINDMAP_CURVE_EDGE_TYPE,
      data: {
        colorIndex: edge.source.depth % DEPTH_COLORS.length,
        sourceDepth: edge.source.depth,
      },
    };
  });

  const graphDimensions = dagreGraph.graph();

  return {
    nodes,
    edges,
    graphWidth: graphDimensions.width ?? 0,
    graphHeight: graphDimensions.height ?? 0,
  };
}
